import { z } from 'zod';

/**
 * F34 — package entitlement grants + the append-only consumption ledger.
 *
 * A purchase is a grant ("client holds N sessions of package X"); it snapshots
 * session_count / duration / price / currency at grant time so a later
 * package-definition edit doesn't shift the grant (F35 reads these for billing).
 *
 * Balance is a DERIVED read — `sessionsRemaining = sessionCountGranted −
 * count(unreversed consumptions)` — never a mutable counter. A consumption row
 * is appended on `booking.completed` AND `booking.marked_no_show` (D3); the
 * UNIQUE(booking_id) DB constraint makes the decrement idempotent under
 * duplicate events. An admin can reverse a consumption (credits the session back).
 */

export const TRAINER_PACKAGE_PURCHASE_STATUS_VALUES = [
  'active',
  'expired',
  'exhausted',
  'cancelled',
] as const;
export type TrainerPackagePurchaseStatus = (typeof TRAINER_PACKAGE_PURCHASE_STATUS_VALUES)[number];
export const TrainerPackagePurchaseStatusSchema = z.enum(TRAINER_PACKAGE_PURCHASE_STATUS_VALUES);

export const TRAINER_PACKAGE_CONSUMPTION_KIND_VALUES = ['completed', 'no_show'] as const;
export type TrainerPackageConsumptionKind =
  (typeof TRAINER_PACKAGE_CONSUMPTION_KIND_VALUES)[number];
export const TrainerPackageConsumptionKindSchema = z.enum(TRAINER_PACKAGE_CONSUMPTION_KIND_VALUES);

const sessionDurationSchema = z.union([
  z.literal(30),
  z.literal(45),
  z.literal(60),
  z.literal(90),
  z.literal(120),
]);

export type TrainerPackagePurchase = {
  readonly id: string;
  readonly trainerUserId: string;
  readonly clientUserId: string;
  readonly packageId: string | null;
  readonly sessionCountGranted: number;
  readonly sessionDurationMinutes: number;
  readonly priceAmount: number;
  readonly priceCurrency: string;
  readonly purchasedAt: string;
  readonly expiresAt: string | null;
  readonly status: TrainerPackagePurchaseStatus;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/** A purchase enriched with its derived remaining balance (the queryable "view"). */
export type TrainerPackageBalance = TrainerPackagePurchase & {
  readonly packageName: string | null;
  readonly sessionsConsumed: number;
  readonly sessionsRemaining: number;
};

export type TrainerPackageConsumption = {
  readonly id: string;
  readonly purchaseId: string;
  readonly bookingId: string;
  readonly kind: TrainerPackageConsumptionKind;
  readonly consumedAt: string;
  readonly reversedAt: string | null;
  readonly reversedByAdminId: string | null;
  readonly reversalReason: string | null;
};

/** Trainer records a grant ("client bought a package off-platform"). */
export const CreateTrainerPackagePurchaseInputSchema = z
  .object({
    clientUserId: z.string().uuid(),
    packageId: z.string().uuid().nullable().optional(),
    sessionCountGranted: z.number().int().min(1).max(1000),
    sessionDurationMinutes: sessionDurationSchema,
    priceAmount: z.number().int().min(0),
    priceCurrency: z.string().length(3).optional(),
    expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
  })
  .strict();
export type CreateTrainerPackagePurchaseInput = z.infer<
  typeof CreateTrainerPackagePurchaseInputSchema
>;

/** Trainer cancels or adjusts a grant. `expired`/`exhausted` are system-derived only. */
export const UpdateTrainerPackagePurchaseInputSchema = z
  .object({
    status: z.enum(['active', 'cancelled']),
    expiresAt: z.string().datetime({ offset: true }).nullable(),
  })
  .partial()
  .strict();
export type UpdateTrainerPackagePurchaseInput = z.infer<
  typeof UpdateTrainerPackagePurchaseInputSchema
>;

/** Admin reverses a completed/no-show consumption (credits the session back). */
export const ReverseTrainerConsumptionInputSchema = z
  .object({ reason: z.string().min(1).max(500) })
  .strict();
export type ReverseTrainerConsumptionInput = z.infer<typeof ReverseTrainerConsumptionInputSchema>;

/**
 * FIX 4.1 (D-D1) — one of the player's grants with a trainer that can still
 * cover a session: `sessionsAvailable` = granted − consumed − reserved by the
 * player's pending or confirmed sessions on it. A trainer booking whose package
 * and session length match takes the oldest-expiring one, at price 0.
 */
export type TrainerPackageCredit = {
  readonly purchaseId: string;
  readonly packageId: string | null;
  readonly packageName: string | null;
  readonly sessionDurationMinutes: number;
  readonly sessionsAvailable: number;
  readonly expiresAt: string | null;
};

/** `GET /me/trainer-package-credits?trainerId=` */
export const TrainerPackageCreditsQuerySchema = z.object({ trainerId: z.string().uuid() }).strict();
export type TrainerPackageCreditsQuery = z.infer<typeof TrainerPackageCreditsQuerySchema>;
